import { Factory, MapPin, MoveRight } from "lucide-react";

import { townLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { PickupArea, Plant } from "@/types/api";

/**
 * The journey in one clean line:  📍 town ┄┄┄→ 🏭 plant
 * Both ends truncate independently, so a long village name can never push the
 * destination off the card — the exact failure the old single-line layout had.
 */
export function RouteLine({
  from,
  to,
  className,
  muted = false,
}: {
  from: PickupArea;
  to: Plant;
  className?: string;
  /** Navy-card variant (MyGroupCard) vs light-card variant (browse). */
  muted?: boolean;
}) {
  return (
    <div className={cn("flex items-center gap-2 text-sm font-medium", className)}>
      <span className="flex min-w-0 shrink items-center gap-1.5">
        <MapPin
          className={cn("size-4 shrink-0", muted ? "text-sidebar-primary" : "text-primary")}
          aria-hidden
        />
        <span className="truncate">{townLabel(from)}</span>
      </span>

      <span
        aria-hidden
        className={cn(
          "min-w-3 flex-1 border-t border-dashed",
          muted ? "border-sidebar-foreground/30" : "border-border",
        )}
      />
      <MoveRight
        aria-hidden
        className={cn(
          "-ml-1.5 size-4 shrink-0",
          muted ? "text-sidebar-foreground/50" : "text-muted-foreground",
        )}
      />

      <span className="flex min-w-0 shrink items-center gap-1.5">
        <Factory
          className={cn(
            "size-4 shrink-0",
            muted ? "text-sidebar-primary" : "text-[oklch(0.6958_0.0821_72.02)]",
          )}
          aria-hidden
        />
        <span className="truncate">{to.name}</span>
      </span>
    </div>
  );
}
